import { useState, useEffect } from "react";
import { Container, Row, Col, Form, Button, InputGroup } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import favicon from "../Utils/assets/img/pugcrm_logo1.png";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import ReCAPTCHA from "react-google-recaptcha";
import "../Utils/assets/bootstrap/css/bootstrap.min.css";
import "../Utils/assets/css/registration.css";
import logo from "../Utils/assets/img/pugcrm_logo_description.png";
import Select from "react-select";
// Phone input lib
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import { getCountries, getStates, getCities } from "../Utils/hooks/location";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import useLocalStorageState from "use-local-storage-state";
import { PostRequest } from "../Utils/hooks/ApiHandler";
import {
  checkDuplicate,
  customStyles,
  useDebounce,
} from "../Utils/helpers/commonhelper";
import { IconCircleX } from "@tabler/icons-react";

const schema = z.object({
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email"),
  company_name: z.string().min(1, "Company name is required"),
  employee_size: z.string().optional(),
  address: z.string().min(1, "Address is required"),
  country: z.string().min(1, "Country is required"),
  state: z.string().min(1, "State is required"),
  city: z.string().min(1, "City is required"),
  recaptcha: z.string().min(1, "Please complete the reCAPTCHA"),
  zipcode: z.string().min(1, "Zipcode is required"),
});

export const Registration = () => {
  const {
    register,
    handleSubmit,
    control,
    trigger,
    setValue,
    watch,
    reset,
    formState: { errors, isValid },
  } = useForm({
    resolver: zodResolver(schema),
    mode: "onTouched",
    defaultValues: {
      employee_size: "A",
      recaptcha: "",
      country: "99",
    },
  });

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("99");
  const [selectedState, setSelectedState] = useState("");
  const [phone, setPhone] = useState("");
  const [validPhone, setValidPhone] = useState(false);
  const [dialCode, setDialCode] = useState("");
  const [nextClicked, setNextClicked] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState(null); // Track reCAPTCHA value
  const rawNum = phone.length > 1 && dialCode && phone.slice(dialCode?.length);
  const navigate = useNavigate();

  // duplicacy with respect to the primary email and contact
  const [duplicateEmailMessage, setDuplicateEmailMessage] = useState(null);
  const [unverifiedUserId, setUnverifiedUserId] = useState(null);
  const [duplicatePhoneMessage, setDuplicatePhoneMessage] = useState(null);
  const [touched, setTouched] = useState(false);
  const email = watch("email");
  const debouncedEmail = useDebounce(email || "", 500);
  const debouncedPhone = useDebounce(phone || "", 500);

  // useeffect navigation if already login
  const [customer] = useLocalStorageState("customer");

  // checking is already authenticated
  const [lastPath] = useLocalStorageState("lastPath");
  useEffect(() => {
    if (
      customer &&
      customer?.user_login === true &&
      customer?.customer_status === "Active"
    ) {
      navigate(lastPath, { replace: true });
    }
    return () => { };
  }, [lastPath]);

  // fetching countries
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const data = await getCountries();
        if (Array.isArray(data)) {
          setCountries(data);
        } else {
          throw new Error("Invalid countries data");
        }
      } catch {
        setCountries([
          { id: "99", country_name: "India" },
          { id: "1", country_name: "USA" },
        ]);
      }
    };
    fetchCountries();
  }, []);

  // fetching states based on country
  useEffect(() => {
    const fetchStates = async () => {
      if (!selectedCountry) return;
      try {
        const data = await getStates(selectedCountry);
        if (Array.isArray(data)) setStates(data);
      } catch {
        setStates([
          { id: "12", state_name: "Maharashtra" },
          { id: "13", state_name: "Delhi" },
        ]);
      }
    };
    fetchStates();
  }, [selectedCountry]);

  // fetching cities based on state
  useEffect(() => {
    const fetchCities = async () => {
      if (!selectedState) return;
      try {
        const data = await getCities(selectedState);
        if (Array.isArray(data)) setCities(data);
      } catch {
        setCities([
          { id: "1", city_name: "Pune" },
          { id: "2", city_name: "Mumbai" },
        ]);
      }
    };
    fetchCities();
  }, [selectedState]);

  // Phone validation only after next is clicked
  useEffect(() => {
    if (nextClicked) {
      setValidPhone(phone.length <= 2);
    }
  }, [phone, nextClicked]);

  // Effect for email duplication check
  useEffect(() => {
    const checkEmail = async () => {
      if (debouncedEmail) {
        setDuplicateEmailMessage(null);
        const isEmailExisting = await checkDuplicate({
          col: "email",
          customer_id: "", // Hardcoded as per example
          validating_val: debouncedEmail,
          contact_id: "", // Hardcoded as per example
          checkFor: "register",
        });
        if (isEmailExisting?.status === "success") {
          setDuplicateEmailMessage("success");
        } else if (isEmailExisting?.status === "unverified") {
          setUnverifiedUserId(isEmailExisting?.data);
          setDuplicateEmailMessage("unverified");
        }
      } else {
        setDuplicateEmailMessage(null);
      }
    };
    checkEmail();
  }, [debouncedEmail]);

  // Effect for phone duplication check
  useEffect(() => {
    const checkPhone = async () => {
      if (debouncedPhone) {
        setDuplicatePhoneMessage(null);
        const isPhoneExisting = await checkDuplicate({
          col: "phone",
          customer_id: "",
          validating_val: rawNum,
          contact_id: "",
          checkFor: "register",
        });
        if (isPhoneExisting) {
          setDuplicatePhoneMessage("Phone number already exists!");
        }
      } else {
        setDuplicatePhoneMessage(null);
      }
    };
    checkPhone();
  }, [debouncedPhone]);

  // handle next button
  const handleNext = async () => {
    setNextClicked(true);

    // Manually validate reCAPTCHA along with other fields
    const isValidFields = await trigger(["first_name", "last_name", "email"]);
    const isPhoneValid = phone.length > 2;
    const isRecaptchaValid = !!recaptchaValue;

    if (isValidFields && isPhoneValid && isRecaptchaValid) {
      setStep(2);
    }
  };

  // final submit
  const onSubmit = async (formdata) => {
    try {
      setLoading(true);
      if (!isValid || !recaptchaValue) return;

      const payload = {
        ...formdata,
        phone: rawNum,
        country_code: dialCode,
        business_domain: "sales crm",
        institute: "sdfsfd",
      };


      // Uncomment for actual API call
      let response = await PostRequest("auth/register", payload, false, {}, {});
      if (response.status === "fail") {
        toast.warning(response.message);
        return;
      }
      reset();
      setPhone("");
      setStep(1);
      toast.success(response.message);
      navigate("/registrationSuccessful", {
        state: {
          email: email
        },
      });
    } catch (error) {
      const message = error.response?.data?.message;
      if (message === "Duplicate Email address") {
        toast.error(
          "This email is already registered. Please use another one."
        );
      } else {
        toast.error(message || "Registration failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle reCAPTCHA change
  const handleRecaptchaChange = (value) => {
    setRecaptchaValue(value);
    setValue("recaptcha", value, { shouldValidate: true });
  };

  const handleDuplicateEmailClicked = async () => {
    try {
      const payload = {};
      const res = await PostRequest(
        `/auth/register/sendRegistrationMail/${unverifiedUserId}/${email}`,
        payload,
        false,
        {},
        {}
      );
      if (res.status === "fail" || res.status !== "success") {
        return res.status || "something went wrong!";
      }
      if (res.status === "success") {
        return toast.success(res.message);
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Container
      fluid
      className="  p-0"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <Row
        className="overflow-hidden bg-white w-100 pe-2 m-0 mainDivs registrations "
        style={{ height: "calc(100vh - 0rem)", padding: "30px 0px" }}
      >
        <div className="BaseBox col-md-7 ps-8">
          {/* <div className="logoImgDivs" style={Styles.logoImgDivs}>
                             
                         </div> */}
          <div>
            <div className="BaseBox-inner">
              <h3 className="StyledBaseText">
                <span className="StyledBaseText">India's No 1</span> Choice for
                Smarter Selling Simplest and Secure CRM Technology.
              </h3>
            </div>
            <div className="BaseBox-second register">
              <div className="BaseBox-second-inner">
                <div className="BaseBox-second-inner-icon">
                  <i className="fa fa-star"></i>
                </div>
                <h6 className="StyledBaseText">
                  Automated Lead{" "}
                  <span style={{ fontSize: "1rem", lineHeight: 1.5 }}>
                    Distribution
                  </span>
                </h6>
              </div>
              <div className="BaseBox-second-inner">
                <div className="BaseBox-second-inner-icon">
                  <i className="fa fa-user-tie"></i>
                </div>
                <h6 className="StyledBaseText">
                  Sales Analytics &{" "}
                  <span style={{ fontSize: "1rem", lineHeight: 1.5 }}>
                    Forcasting
                  </span>
                </h6>
              </div>
              <div className="BaseBox-second-inner">
                <div className="BaseBox-second-inner-icon">
                  <i className="fa fa-briefcase"></i>
                </div>
                <h6 className="StyledBaseText">
                  Social Media{" "}
                  <span style={{ fontSize: "1rem", lineHeight: 1.5 }}>
                    Integration
                  </span>
                </h6>
              </div>
              <div className="BaseBox-second-inner">
                <div className="BaseBox-second-inner-icon">
                  <i className="fa fa-earth-asia"></i>
                </div>
                <h6 className="StyledBaseText">
                  Analytics{" "}
                  <span style={{ fontSize: "1rem", lineHeight: 1.5 }}>
                    Dashboard
                  </span>
                </h6>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Registration Form */}
        <Col md={5} className="py-0 h-100 col-md-4 pe-4 p-0">
          <div className="formDiv w-100 h-100 py-3 px-3">
            <div className="innerFormDiv w-100">
              <div className="text-start ps-4 mb-4">
                <img src={favicon} alt="Logo" className="favicon" />

                <p
                  className="fw-bold bigHeading mt-4"
                  style={{ color: "black", fontSize: 28, lineHeight: 1.3 }}
                >
                  Welcome to PugCRM!{" "}
                </p>
                <p
                  className="smallHeading mt-2"
                  style={{
                    fontSize: 14,
                    marginTop: 14,
                    fontWeight: 400,
                    letterSpacing: 0,
                  }}
                >
                  Enter your company details to continue registration.
                </p>
              </div>

              <Form
                onSubmit={handleSubmit(onSubmit)}
                style={{ textAlign: "justify", padding: "0px 25px" }}
                className="form-container"
              >
                {step === 1 && (
                  <>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-2  d-flex flex-column focus-z-index position-relative">
                          <Form.Label
                            style={{ marginBottom: 1 }}
                            className=" fw-lighter "
                          >
                            First Name <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            {...register("first_name", { required: true })}
                          />
                          <small className="text-danger">
                            {errors.first_name?.message}
                          </small>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-2  d-flex flex-column focus-z-index position-relative">
                          <Form.Label
                            style={{ marginBottom: 1 }}
                            className=" fw-lighter "
                          >
                            Last Name <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            {...register("last_name", { required: true })}
                          />
                          <small className="text-danger">
                            {errors.last_name?.message}
                          </small>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-2">
                      <Col md={6}>
                        <Form.Group className="mb-2 d-flex flex-column focus-z-index position-relative">
                          <Form.Label
                            style={{ marginBottom: 1 }}
                            className=" fw-lighter  "
                          >
                            Email <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control type="email" {...register("email")} />
                          {duplicateEmailMessage && (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                fontSize: "12px",
                                gap: 2,
                                paddingTop: "4px",
                                marginBottom: "10px",
                              }}
                              className="text-danger"
                            >
                              {/* <IconCircleX
                                stroke={2}
                                style={{ width: "14px", height: "14px" }}
                              /> */}
                              {/* {duplicateEmailMessage ? "sucu"} */}
                              {duplicateEmailMessage === "success" ? (
                                "Email already exist!"
                              ) : (
                                <>
                                  <span>
                                    Email already exist. Please verify your email,
                                    <span
                                      style={{ cursor: "pointer" }}
                                      className="text-primary "
                                      onClick={() => {
                                        handleDuplicateEmailClicked();
                                      }}
                                    > Send verification email again
                                    </span>
                                  </span>
                                </>
                              )}
                            </div>
                          )}
                          <small className="text-danger">
                            {errors.email?.message}
                          </small>
                        </Form.Group>
                      </Col>
                      <Col md={6} className="registrationDivsItem">
                        <InputGroup style={{ display: "block", width: "100%" }}>
                          <Form.Label>
                            Phone Number <span className="text-danger">*</span>
                          </Form.Label>

                          <div className="my-phone-input">
                            <PhoneInput
                              className="my-phone-field"
                              country="in"
                              countryCodeEditable={false}
                              value={phone}
                              onChange={(value, country) => {
                                setPhone(value);
                                setDialCode(country.dialCode);

                                const phoneWithoutCode = value.replace(
                                  country.dialCode,
                                  ""
                                );

                                if (phoneWithoutCode.length === 10) {
                                  setValidPhone(true);
                                } else {
                                  setValidPhone(false);
                                }
                              }}
                              onBlur={() => {
                                setTouched(true);
                              }}
                              inputStyle={{
                                width: "100%",
                                height: "52px",
                                border: "none",
                              }}
                            />
                          </div>

                          {/* Duplicate error */}
                          {duplicatePhoneMessage && (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                fontSize: "12px",
                                gap: 2,
                                paddingTop: "4px",
                                marginBottom: "6px",
                              }}
                              className="text-danger"
                            >
                              <IconCircleX
                                stroke={2}
                                style={{ width: "10px", height: "10px" }}
                              />
                              {duplicatePhoneMessage}
                            </div>
                          )}
                          {/* Required */}
                          {touched && !phone && (
                            <small className="text-danger  " style={{ fontSize: "12px" }}>
                              Phone Number is Required
                            </small>
                          )}

                          {/* Invalid */}
                          {touched && phone && !validPhone && (
                            <small className="text-danger">
                              Invalid Phone Number
                            </small>
                          )}
                        </InputGroup>
                      </Col>
                      <Col md={7}>
                        <Form.Group className="mb-4">
                          {/* <Form.Label
                            style={{ marginBottom: 1 }}
                            className=" fw-lighter "
                          >
                            Re-Captcha <span className="text-danger">*</span>
                          </Form.Label> */}
                          <ReCAPTCHA
                            sitekey="6LcFFaEqAAAAABIUCh2aImy_O2gXSLON_OwargwA"
                            onChange={handleRecaptchaChange}
                          />
                          <small className="text-danger">
                            {nextClicked &&
                              !recaptchaValue &&
                              "Please complete the reCAPTCHA"}
                          </small>
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-center">
                      <Button
                        type="button"
                        variant="action-btn"
                        className="w-50 btn"
                        onClick={handleNext}
                        disabled={
                          !recaptchaValue ||
                          duplicateEmailMessage === "success" ||
                          duplicateEmailMessage === "unverified" ||
                          duplicatePhoneMessage || !validPhone
                        }
                      >
                        Next
                      </Button>
                    </div>

                    <div
                      style={{ cursor: "pointer" }}
                      className=" d-flex align-self-center justify-content-center gap-2 small text-center mt-4 text-secondary"
                    >
                      You Have An Account?{" "}
                      <p
                        onClick={() => navigate("/login")}
                        className="text-primary fw-medium text-decoration-none"
                      >
                        Log In
                      </p>
                    </div>
                  </>
                )}

                {step === 2 && (
                  <>
                    <Row className="mb-2">
                      <Col md={6}>
                        <Form.Group className="mb-3 d-flex flex-column focus-z-index position-relative">
                          <Form.Label
                            style={{ marginBottom: 1 }}
                            className=" fw-lighter "
                          >
                            Company Name <span className="text-danger">*</span>
                          </Form.Label>
                          <InputGroup>
                            <Form.Control
                              type="text"
                              {...register("company_name")}
                            />
                          </InputGroup>
                          <small className="text-danger">
                            {errors.company_name?.message}
                          </small>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3 d-flex flex-column focus-z-index position-relative">
                          <Form.Label>Employee Size</Form.Label>

                          <Controller
                            name="employee_size"
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                options={[
                                  { value: "A", label: "Self-employed" },
                                  { value: "B", label: "1-10 employees" },
                                  { value: "C", label: "11-50 employees" },
                                  { value: "D", label: "51-200 employees" },
                                  { value: "E", label: "201-500 employees" },
                                  { value: "F", label: "501-1000 employees" },
                                  { value: "G", label: "1001-5000 employees" },
                                  {
                                    value: "H",
                                    label: "5001-10,000 employees",
                                  },
                                  { value: "I", label: "10,001+ employees" },
                                ]}
                                classNamePrefix="react-select"
                                styles={customStyles}
                                placeholder="Select employee size"
                                value={
                                  field.value
                                    ? {
                                      value: field.value,
                                      label: [
                                        "Self-employed",
                                        "1-10 employees",
                                        "11-50 employees",
                                        "51-200 employees",
                                        "201-500 employees",
                                        "501-1000 employees",
                                        "1001-5000 employees",
                                        "5001-10,000 employees",
                                        "10,001+ employees",
                                      ][field.value.charCodeAt(0) - 65],
                                    }
                                    : null
                                }
                                onChange={(selected) =>
                                  field.onChange(selected?.value)
                                }
                              />
                            )}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-2 ">
                      <Col md={6}>
                        <Form.Group className="mb-3 d-flex flex-column focus-z-index position-relative">
                          <Form.Label
                            style={{ marginBottom: 1 }}
                            className="fw-lighter"
                          >
                            Country <span className="text-danger">*</span>
                          </Form.Label>

                          <Controller
                            name="country"
                            control={control}
                            rules={{ required: "Country is required" }}
                            render={({ field }) => (
                              <Select
                                {...field}
                                options={countries.map((country) => ({
                                  value: country.id,
                                  label:
                                    country.nicename || country.country_name,
                                }))}
                                placeholder="Select Country"
                                classNamePrefix="react-select"
                                styles={customStyles}
                                value={
                                  field.value
                                    ? {
                                      value: field.value,
                                      label:
                                        countries.find(
                                          (c) => c.id === field.value
                                        )?.nicename ||
                                        countries.find(
                                          (c) => c.id === field.value
                                        )?.country_name,
                                    }
                                    : null
                                }
                                onChange={(selected) => {
                                  const val = selected?.value || "";

                                  // update react-hook-form
                                  field.onChange(val);

                                  // your custom logic (same as before)
                                  setSelectedCountry(val);
                                  setSelectedState("");
                                  setValue("state", "");
                                  setValue("city", "");
                                  setStates([]);
                                  setCities([]);
                                }}
                              />
                            )}
                          />

                          <small className="text-danger">
                            {errors.country?.message}
                          </small>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3 d-flex flex-column focus-z-index position-relative">
                          <Form.Label
                            style={{ marginBottom: 1 }}
                            className="fw-lighter"
                          >
                            State <span className="text-danger">*</span>
                          </Form.Label>

                          <Controller
                            name="state"
                            control={control}
                            rules={{ required: "State is required" }}
                            render={({ field }) => (
                              <Select
                                {...field}
                                options={states.map((state) => ({
                                  value: state.id,
                                  label: state.state_name,
                                }))}
                                placeholder="Select State"
                                classNamePrefix="react-select"
                                styles={customStyles}
                                value={
                                  field.value
                                    ? {
                                      value: field.value,
                                      label: states.find(
                                        (s) => s.id === field.value
                                      )?.state_name,
                                    }
                                    : null
                                }
                                onChange={(selected) => {
                                  const val = selected?.value || "";

                                  // Update react-hook-form
                                  field.onChange(val);

                                  // Your same logic
                                  setSelectedState(val);
                                  setValue("city", "");
                                  setCities([]);
                                }}
                              />
                            )}
                          />

                          <small className="text-danger">
                            {errors.state?.message}
                          </small>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-2">
                      <Col md={6}>
                        <Form.Group className="mb-3 d-flex flex-column focus-z-index position-relative">
                          <Form.Label
                            style={{ marginBottom: 1 }}
                            className="fw-lighter"
                          >
                            City <span className="text-danger">*</span>
                          </Form.Label>

                          <Controller
                            name="city"
                            control={control}
                            rules={{ required: "City is required" }}
                            render={({ field }) => (
                              <Select
                                {...field}
                                options={cities.map((city) => ({
                                  value: city.id,
                                  label: city.city_name,
                                }))}
                                placeholder="Select City"
                                styles={customStyles}
                                classNamePrefix="react-select"
                                value={
                                  field.value
                                    ? {
                                      value: field.value,
                                      label: cities.find(
                                        (c) => c.id === field.value
                                      )?.city_name,
                                    }
                                    : null
                                }
                                onChange={(selected) => {
                                  const val = selected?.value || "";
                                  field.onChange(val); // update react-hook-form
                                }}
                              />
                            )}
                          />

                          <small className="text-danger">
                            {errors.city?.message}
                          </small>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        {/* Zipcode */}
                        <Form.Group className="mb-3 focus-z-index position-relative">
                          <Form.Label>
                            Zipcode <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            className="form-control"
                            placeholder="Enter Zipcode"
                            {...register("zipcode", { required: true })}
                          />
                          {errors?.zipcode && (
                            <p className="text-danger">
                              {errors?.zipcode?.message}{" "}
                            </p>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="mb-2">
                      <Col md={12}>
                        <Form.Group className="mb-3 d-flex flex-column focus-z-index position-relative">
                          <Form.Label
                            style={{ marginBottom: 1 }}
                            className=" fw-lighter "
                          >
                            Address <span className="text-danger">*</span>
                          </Form.Label>
                          <InputGroup>
                            <Form.Control
                              as="textarea"
                              {...register("address")}
                              style={{ height: "38px", resize: "none" }}
                            />
                          </InputGroup>
                          <small className="text-danger">
                            {errors.address?.message}
                          </small>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group className="mb-4 form-check">
                      <Form.Check
                        type="checkbox"
                        required
                      // label={

                      // }
                      />
                      <span className="ms-2">
                        I agree to the{" "}
                        <a
                          href="https://www.pandalms.com/terms-conditions"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Terms & Conditions
                        </a>{" "}
                        and{" "}
                        <a
                          href="https://www.pandalms.com/privacy-policy"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Privacy Policy
                        </a>
                      </span>
                    </Form.Group>

                    <div className="d-flex justify-content-center  gap-2 ">
                      <Button variant="action-btn" onClick={() => setStep(1)}>
                        Back
                      </Button>
                      <Button
                        type="submit"
                        variant="action-btn"
                        disabled={loading}
                      >
                        {loading ? "Creating..." : "Create Account"}
                      </Button>
                    </div>
                  </>
                )}
              </Form>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

const Styles = {
  logoImgDivs: {
    background: `URL(${logo})`,
  },
};
